import { useLoading } from "@/hooks/use-loading";
import { create } from "zustand";
import { apiClient } from "../http";
import {
  createPrimitive,
  destroyPrimitive,
  fakePagination,
  loadingKey,
  paginateArray,
  updatePrimitive,
  withLoading,
} from "./primitives";
import type {
  BaseResourceStore,
  FetchOptions,
  ID,
  Operation,
  Pagination,
} from "./type";
import { pick } from "@/lib/utils";
import { useAuth } from "@/store/Auth";

export const initResourceStore = <T>(
  index: string,
  set: any,
  get: any
): BaseResourceStore<T> => {
  return {
    stats: {},
    items: [],
    pagination: fakePagination([]) as Pagination,
    result: [],

    fetch: (
      params: any = { page: 1, per_page: 15 },
      options: FetchOptions = { save: true }
    ) => {
      const session = useAuth.getState().session;

      return apiClient()
        .get(`${index}`, {
          params: {
            shop_id: session?.currentShop?.id,
            business_id: session?.business?.id,
            ...params,
          },
        })
        .then((res) => {
          if (options.save) {
            // Paginate if the data are not paginate.
            if (!res.data.hasOwnProperty("current_page")) {
              get().setItems(res.data as T[]);
              get().setResult(res.data as T[]);
              get().paginate(res.data, pick(params, ["page", "per_page"]));
              return Promise.resolve(
                res.data.map((item: T) => get().transform(item))
              );
            } else {
              set({
                pagination: res.data as Pagination<T>,
                items: res.data.data as T[],
                result: res.data.data as T[],
              });
              return Promise.resolve(
                res.data.data.map((item: T) => get().transform(item))
              );
            }
          } else {
            if (!res.data.hasOwnProperty("current_page")) {
              return Promise.resolve(
                res.data.map((item: T) => get().transform(item))
              );
            } else {
              return Promise.resolve(
                res.data.data.map((item: T) => get().transform(item))
              );
            }
          }
        });
    },

    getStats: (start_date: string, end_date: string) => {
      const session = useAuth.getState().session;

      return apiClient()
        .get(`${index}/stats`, {
          params: {
            shop_id: session?.currentShop?.id,
            start_date,
            end_date,
          },
        })
        .then((res: any) => {
          set(() => ({
            stats: res.data,
          }));
          return Promise.resolve(res.data);
        });
    },

    fetchOne: (id: ID) => {
      const session = useAuth.getState().session;

      return apiClient()
        .get(`${index}/${id}`, {
          params: {
            shop_id: session?.shop?.id,
          },
        })
        .then((res) => {
          get().setCurrent(res.data);
          return Promise.resolve(res.data);
        });
    },

    transform: (item: T) => item as T,

    setCurrent: (inputs: Partial<T> | undefined) =>
      set((state: any) => ({
        current:
          inputs == undefined
            ? undefined
            : get().transform({
                ...state.current,
                ...inputs,
              }),
      })),

    paginate: (items: T[], options?: { page?: number; per_page?: number }) => {
      let page = 1;
      let per_page = 15;
      if (options) {
        if (options.page) page = options.page;
        if (options.per_page) per_page = options.per_page;
      }
      const pagination = paginateArray(items, { page, per_page });
      set(() => ({
        pagination: {
          ...pagination,
          data: pagination.data.map((item) => get().transform(item)),
        },
      }));
    },
    setResult: (items: T[]) => {
      set(() => ({
        // We do not apply transform here. Because it's result for static search.
        result: items,
      }));
    },
    setItems: (items: T[]) => {
      set(() => ({
        items: items.map((item) => get().transform(item)),
      }));
    },
    add: (item: T, addFirst: boolean = false) => {
      const { items, setItems } = get();
      setItems(addFirst ? [item, ...items] : [...items, item]);
    },
    filter: (predicate: (item: T, index: number) => boolean) => {
      const { items, setItems } = get();
      setItems(items.filter(predicate));
    },

    remove: (index: number) => {
      get().filter((_: T, i: number) => i != index);
    },

    sync: (data: Partial<T>, predicate: (item: T) => boolean) => {
      const { items, setItems } = get();
      setItems(
        items.map((item: T) => (predicate(item) ? { ...item, ...data } : item))
      );
    },

    syncWithId: (data: Partial<T>) => {
      get().sync(data, (item: T) => (item as any).id == (data as any).id);
      if (get().current?.id == (data as any)?.id) {
        get().setCurrent(data);
      }
    },

    update: (id: ID, data: Partial<T> | FormData, options?: any) => {
      return withLoading(`update_${index}_${id}`, async () => {
        const updated: any = await updatePrimitive<T>({
          data,
          index,
          id,
          options,
        });
        if (options?.sync !== false) {
          get().syncWithId(updated);
        }
        return updated;
      });
    },

    create: (data: Partial<T> | FormData, options?: any) => {
      return withLoading(`create_${index}`, async () => {
        const created: any = await createPrimitive<T>({ index, data, options });
        if (options?.sync !== false) {
          get().add(created, options?.addFirst === true);
        }
        return created;
      });
    },

    destroy: (id: ID, options?: any) => {
      return withLoading(loadingKey("destroy", index, id), async () => {
        const deleted = await destroyPrimitive({ index, id, options });
        if (options?.sync !== false) {
          get().filter((item: T) => (item as any).id !== id);
        }
        return deleted;
      });
    },

    loading: (operation: Operation, id?: ID) => {
      return useLoading.getState().status[loadingKey(operation, index, id)];
    },
    // Update current will always sync.
    updateCurrent: (data: Partial<T> | FormData, options?: any) => {
      const current = get().current;
      if (!current) {
        return Promise.reject("No current element selected.");
      }
      return get().update(current.id, data, { ...options, sync: true });
    },

    destroyCurrent: (options?: any) => {
      const current = get().current;
      if (!current) {
        return Promise.reject("No current element selected.");
      }

      return get().destroy(current.id, options);
    },

    loadingCurrent: (operation: Operation) => {
      const current = get().current;
      if (!current) {
        return false;
      } else {
        return useLoading.getState().status[
          loadingKey(operation, index, current.id)
        ];
      }
    },
  };
};

export const createResourceStore = <T, S = {}>(
  index: string,
  stateSlice?: (set: any, get: any) => S
) => {
  return create<BaseResourceStore<T> & S>((set, get) => ({
    ...initResourceStore<T>(index, set, get),
    ...(stateSlice ? stateSlice(set, get) : ({} as S)),
  }));
};
